import { createFileRoute } from "@tanstack/react-router";
import React from "react";
import { z } from "zod";
import { JobOffer } from "@/features/job-offer/forms/job-offer";
import { calculateDurationMinutes } from "@/features/job-offer/utils/job-offer-data";
import { ScheduleType } from "@api/shared-runtime/enums";

export const Route = createFileRoute(
  "/_org/org/bu/$organizationBusinessUnitId/jobs/offer/create/",
)({
  component: CreateJobOfferPage,
  validateSearch: z.object({
    id: z.string().optional(),
    startDate: z
      .string()
      .regex(/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/)
      .optional(),
    endDate: z
      .string()
      .regex(/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/)
      .optional(),
    startTime: z
      .string()
      .regex(/^[0-9]{2}:[0-9]{2}$/)
      .optional(),
    endTime: z
      .string()
      .regex(/^[0-9]{2}:[0-9]{2}$/)
      .optional(),
  }),
});

/**
 * Calculate end time that is 8 hours after start time.
 * Handles overnight shifts (wraps around midnight).
 */
function calculateDefaultEndTime(startTime: string): string {
  const [hours, minutes] = startTime.split(":").map(Number);
  const endHours = (hours + 8) % 24;
  return `${String(endHours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function CreateJobOfferPage() {
  const search = Route.useSearch();

  console.log("[CreateJobOffer] search params:", search);

  const defaultSchedules = React.useMemo(() => {
    if (!search.startDate) {
      return undefined;
    }

    // Default start time to 9:00 AM if not provided
    const startTime = search.startTime ?? "09:00";
    // If end time not provided, default to 8 hours after start time
    const endTime = search.endTime ?? calculateDefaultEndTime(startTime);
    const durationMinutes = calculateDurationMinutes(startTime, endTime);

    console.log("[CreateJobOffer] times:", {
      startTime,
      endTime,
      durationMinutes,
    });

    // Combine date and time into ISO datetime string
    const startsAt = `${search.startDate}T${startTime}:00`;

    return [
      {
        startsAt,
        durationMinutes,
        rrule: null, // Single occurrence
        scheduleType: ScheduleType.Regular,
      },
    ];
  }, [search.startDate, search.endDate, search.startTime, search.endTime]);

  return <JobOffer defaultSchedules={defaultSchedules} />;
}
